import React, { useState, Suspense, useEffect } from 'react';
import teamstyles from './css/teamstyles.module.css';
import logo from './img/logo_new.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench, faUserTie, faMagnifyingGlass,faSeedling, faVideo, faPeopleGroup,faUserGraduate, faBroadcastTower, faCogs, faHeartPulse, faHammer, faSection, faScaleBalanced, faComments } from '@fortawesome/free-solid-svg-icons';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useLanguage } from './contexts/LanguageContext';
import LanguageSelector from './components/LanguageSelector';
import './css/DonateButton.css';
import LazyImage from './components/LazyImage';

const DepartmentComponents = {
  HResources: React.lazy(() => import('./departments/HResources').catch(() => ({ default: () => <div>Failed to load HResources</div> }))),
  Production: React.lazy(() => import('./departments/Production').catch(() => ({ default: () => <div>Failed to load Production</div> }))),
  Communication: React.lazy(() => import('./departments/Communication').catch(() => ({ default: () => <div>Failed to load Communication</div> }))),
  Academic: React.lazy(() => import('./departments/Academic').catch(() => ({ default: () => <div>Failed to load Academic</div> }))),
  Finances: React.lazy(() => import('./departments/Finances').catch(() => ({ default: () => <div>Failed to load Finances</div> }))),
  Legal: React.lazy(() => import('./departments/Legal').catch(() => ({ default: () => <div>Failed to load Legal</div> }))),
  Education: React.lazy(() => import('./departments/Education').catch(() => ({ default: () => <div>Failed to load Education</div> }))),
  Health: React.lazy(() => import('./departments/Health').catch(() => ({ default: () => <div>Failed to load Health</div> }))),
  Operation: React.lazy(() => import('./departments/Operation').catch(() => ({ default: () => <div>Failed to load Operation</div> }))),
  Environment: React.lazy(() => import('./departments/Environment').catch(() => ({ default: () => <div>Failed to load Environment</div> }))),
  Administration: React.lazy(() => import('./departments/Administration').catch(() => ({ default: () => <div>Failed to load Administration</div> }))),
  Marketing: React.lazy(() => import('./departments/Marketing').catch(() => ({ default: () => <div>Failed to load Marketing</div> }))),
};

const Team = ({ onBack, onShowDonate }) => {
  const { t, tHtml } = useLanguage();
  
  useEffect(() => {
    document.body.classList.add('team-section');
    return () => document.body.classList.remove('team-section');
  }, []);

  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [showTransition, setShowTransition] = useState(false);
  const [showDepartments, setShowDepartments] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [showSections, setShowSections] = useState(true);
  const [showOurStoryExpanded, setShowOurStoryExpanded] = useState(false);
  const [ourStoryPage, setOurStoryPage] = useState(0);

  const sections = [
    {
      title: t('team.sections.whoWeAre.title'),
      content: tHtml('team.sections.whoWeAre.content')    
    },
    {
      title: t('team.sections.ourStory.title'), 
      content: tHtml('team.sections.ourStory.content')
    },
    {
      title: t('team.sections.whatWeDo.title'),
      content: tHtml('team.sections.whatWeDo.content')
    },
    {
      title: t('team.sections.mission.title'),
      content: tHtml('team.sections.mission.content'),
      lottie: (
        <DotLottieReact
          src="https://lottie.host/4781c2f0-a6b0-4493-9dc2-3d5dc614e529/rof1wzWkuP.lottie"
          loop
          autoplay
          className={teamstyles["lottie1"]}
          />
      )
    },
    {
      title: t('team.sections.vision.title'),
      content: tHtml('team.sections.vision.content'),
      lottie: (
      <DotLottieReact
      src="https://lottie.host/ab5568ed-02dc-4eb5-8f96-23c5ed3761d0/lk5NhJ3lhr.lottie"
      loop
      autoplay
      className={teamstyles["lottie2"]}
    />
      )
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!showSections) return;
      
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      
      // Calculate current section based on scroll position
      const sectionIndex = Math.min(
        Math.floor(scrollPosition / windowHeight),
        sections.length - 1
      );
      
      if (sectionIndex !== currentSection) {
        setCurrentSection(sectionIndex);
      }
    };

    // Set proper height and scroll position when sections become visible
    if (showSections) {
      document.body.style.height = `${window.innerHeight * sections.length}px`;
    } else {
      document.body.style.height = 'auto';
    }
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentSection, showSections]);


  const handleToggleTeam = () => {
    if (!showTransition) {
      setShowTransition(true);
      setTimeout(() => {
        setShowDepartments(true);
        setShowSections(false);
        setShowOurStoryExpanded(false); // Ensure story is hidden when showing team
        setFadeIn(true);
        document.body.style.height = 'auto';
      }, 0);
    } else {
      setFadeIn(false);
      setTimeout(() => {
        setShowDepartments(false);
        setShowSections(true);
        setShowTransition(false);
        // Clean up URL by removing hash fragments
        if (window.location.hash) {
          window.history.replaceState(null, null, window.location.pathname + window.location.search);
        }
      }, 0);
    }
  };

  const handleSelectDepartment = (department) => {
    document.body.style.height = `3000px`; 
    setSelectedDepartment(department);
  }

  const handleBackToTeam = () => {
    setSelectedDepartment(null);
    document.body.style.height = ''; // Reset body height when unmounting
  };

  const icons = {
    Administration: faUserTie,
    Production: faVideo,
    Communication: faComments,
    HResources: faPeopleGroup,
    Education: faUserGraduate,
    Academic: faMagnifyingGlass,
    Finances: faScaleBalanced,
    Legal: faSection,
    Health: faHeartPulse,
    Environment: faSeedling,
    Operation: faWrench,
    Marketing: faBroadcastTower
  };

  const DepartmentComponent = DepartmentComponents[selectedDepartment];

  return (
    <div className={teamstyles["team-container"]}>
      <LazyImage className={`${teamstyles.logo} ${selectedDepartment ? teamstyles.fixed : ''}`} src={logo} alt="Logo" />
      <div className="fixed-buttons-container">
        <button 
          className="donatefixed-button"
          onClick={onShowDonate}
          style={{ display: selectedDepartment ? 'none' : 'block' }}
        >
          Donate
        </button>
        <LanguageSelector />
      </div>
      {!selectedDepartment ? (
        <>
          <div className={`${teamstyles.sectionsContainer} ${!showSections ? teamstyles.hidden : ''}`}>
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`
                  ${teamstyles.section} 
                  ${index === currentSection ? teamstyles.active : ''}
                  ${index < currentSection ? teamstyles.past : ''}
                  ${index > currentSection ? teamstyles.future : ''}
                `}
              >
                <h1>{section.title}</h1>
                <div className={teamstyles.contentWrapper}>
                  <div 
                    dangerouslySetInnerHTML={section.content} 
                  />
                  {section.title === t('team.sections.ourStory.title') && !showOurStoryExpanded && (
                    <button 
                      className={teamstyles.moreButton}
                      onClick={() => {
                        setShowOurStoryExpanded(true);
                        setShowSections(false);
                        document.body.style.height = 'auto';
                        // Clean up URL by removing hash fragments
                        if (window.location.hash) {
                          window.history.replaceState(null, null, window.location.pathname + window.location.search);
                        }
                      }}
                    >
                      {t('team.sections.ourStory.readMore')}
                    </button>
                  )}
                </div>
                {section.lottie && section.lottie}
              </div>
            ))}
          </div>
          {showTransition && (
            <div className={`${teamstyles["transition-screen"]} ${fadeIn ? teamstyles["expand"] : teamstyles["shrink"]}`}></div>
          )}
          {showDepartments && (
            <div className={`${teamstyles["departments-container"]} ${fadeIn ? teamstyles["fade-in"] : ""}`}>
              {Object.keys(icons).map((label, index) => (
                <button
                  key={label}
                  className={teamstyles["department-button"]}
                  style={{ animationDelay: `${index * 0.3}s` }}
                  onClick={() => handleSelectDepartment(label)}
                >
                  <FontAwesomeIcon className={teamstyles.icon} icon={icons[label]} size="3x" />
                  <p>{label}</p>
                </button>
              ))}
            </div>
          )}
          {/* <button
          className={`btn btn-outline ${teamstyles["teambutton"]}`}
          onClick={handleToggleTeam}
          >
            {showDepartments ? 'Back' : 'See our Team'}
          </button>  */}
        </>
      ) : (
        <Suspense fallback={<div>Loading department...</div>}>
          {DepartmentComponent && <DepartmentComponent onBack={handleBackToTeam} />}
        </Suspense>
      )}
      <div className={`${teamstyles["our-story-expanded"]} ${showOurStoryExpanded ? `${teamstyles["visible"]}` : ''}`}>
          <div className={teamstyles["story-content"]}>
            <h1 style={{fontFamily:"yourngo_title"}}>{t('team.expandedStory.title')}</h1>
            <p style={{lineHeight:"1px", color:"#E17200"}}><b>{t('team.expandedStory.subtitle1')}</b></p> <p style={{color:"#064C86"}}><b>{t('team.expandedStory.subtitle2')}</b></p>
             <p dangerouslySetInnerHTML={tHtml('team.expandedStory.paragraph1')}></p>
              <LazyImage src={require('./img/IMG_1312.jpeg')} alt="Story illustration" />
              <p dangerouslySetInnerHTML={tHtml('team.expandedStory.paragraph2')}></p>
              <LazyImage src={require('./img/Our_story_2.jpeg')} alt="Story illustration" />
            <p dangerouslySetInnerHTML={tHtml('team.expandedStory.paragraph3')}></p>
          <div style={{display:"flex", gap:"10px", width:"50%"}}>
          <LazyImage src={require('./img/story3.1.jpg')} alt="Story illustration" />
            <LazyImage src={require('./img/story3.2.jpg')} alt="Story illustration" />
            </div>
            <p dangerouslySetInnerHTML={tHtml('team.expandedStory.paragraph4')}></p>
            <div style={{display:"flex", gap:"10px", width:"50%"}}>
            <LazyImage src={require('./img/story3.3.jpeg')} alt="Story illustration" />
            <LazyImage src={require('./img/story3.4.jpg')} alt="Story illustration" />
            </div>
            <p dangerouslySetInnerHTML={tHtml('team.expandedStory.paragraph5')}></p>
            <LazyImage src={require('./img/OSC02292.jpeg')} alt="Story illustration" />
          </div>
        </div>
      
      {showOurStoryExpanded && (
        <button
        className={`btn btn-outline ${teamstyles["backbutton"]}`}
        onClick={() => {
            setShowOurStoryExpanded(false);
            setShowSections(true);
            document.body.style.height = `${window.innerHeight * sections.length}px`;
            // Clean up URL by removing hash fragments
            if (window.location.hash) {
              window.history.replaceState(null, null, window.location.pathname + window.location.search);
            }
          }}
          style={{ 
            display: showDepartments ? 'none' : 'block' // Hide when team is shown
          }}
        >
          {t('team.expandedStory.back')}
        </button>
      )}
    </div>
  );
};

export default Team;
